import type React from "react";
import {Fragment, memo, useCallback} from "react";

import {useView} from "../../hooks/useView";
import {CloseIcon} from "../../react/components/Icons";
import {isReactElement} from "../../react/utils/isReactElement";
import Drawer from "../Drawer";
import {Button, ButtonSpinner, ButtonText} from "../ui/button";
import {Heading} from "../ui/heading";
import {HStack} from "../ui/hstack";
import type {IModalProps} from "../ui/modal";
import {
    ModalBackdrop,
    ModalBodyUI,
    ModalCloseButton,
    ModalContent,
    ModalFooterUI,
    ModalHeaderUI,
    ModalUI
} from "../ui/modal";
import {Text} from "../ui/text";
import {Tooltip} from "../ui/tooltip";
import {VStack} from "../ui/vstack";

import ModalLayout from "./ModalLayout";

export type Props = {
    isOpen: boolean;
    children?: React.ReactNode;
    title: string;
    subTitle?: React.ReactNode;
    footer?: React.ReactNode;
    isLoading?: boolean;
    btnTextCancel?: string;
    btnTextOk?: string;
    btnTextReset?: string;
    responsive?: boolean;
    size?: IModalProps["size"];
    useDefaultDivider?: boolean;
    isPrimaryButtonDisabled?: boolean;
    testID?: string;
    onClose?: () => void;
    onSubmit?: () => void;
    onReset?: () => void;
} & StrictUnion<
    | {
          useAsDrawer: true;
          isContentLoading?: boolean;
          rightTitleElement?: React.ReactNode;
      }
    | {
          useAsDrawer?: false;
      }
>;

const Modal: React.FC<Props> = ({
    isOpen,
    children,
    footer,
    title,
    subTitle,
    isLoading,
    btnTextCancel = "Cancel",
    btnTextOk = "Save",
    btnTextReset = "Reset",
    responsive = true,
    useAsDrawer = false,
    isContentLoading = false,
    testID = "modal",
    size: sizeProp,
    useDefaultDivider,
    rightTitleElement,
    isPrimaryButtonDisabled,
    onClose,
    onSubmit,
    onReset
}) => {
    const view = useView();

    const handleClose = useCallback(() => {
        // ensure not to call with any arguments as same as the declared type
        onClose?.();
    }, [onClose]);

    const hasFooter = !!footer || typeof onSubmit === "function" || typeof onReset === "function";

    if ((view === "native" || view === "webMobile" || useAsDrawer) && responsive) {
        const btnPrimary = onSubmit && (
            <Button isDisabled={isPrimaryButtonDisabled} onPress={onSubmit}>
                {isLoading ? <ButtonSpinner /> : <ButtonText>{btnTextOk}</ButtonText>}
            </Button>
        );

        // on app this should always be in a navigation route with modal style
        const content = rightTitleElement ? (
            <ModalLayout
                footer={footer}
                isLoading={isContentLoading}
                rightTitleElement={rightTitleElement}
                subTitle={subTitle}
                title={title}
                useDefaultDivider={useDefaultDivider}
                onHide={onClose}
            >
                {children}
            </ModalLayout>
        ) : (
            <ModalLayout
                footer={footer}
                isLoading={isContentLoading}
                primaryButton={btnPrimary}
                subTitle={subTitle}
                title={title}
                useDefaultDivider={useDefaultDivider}
                onHide={onClose}
            >
                {children}
            </ModalLayout>
        );
        if (view === "native") {
            return content;
        }
        return (
            <Drawer isOpen={isOpen} onClose={onClose}>
                {content}
            </Drawer>
        );
    }

    return (
        <ModalUI isOpen={isOpen} size={sizeProp} testID={testID} onClose={onClose}>
            <ModalBackdrop />
            <ModalContent testID="modal-content">
                {title && (
                    <ModalHeaderUI hasFooter={hasFooter}>
                        <VStack className="w-full" space="xs">
                            <HStack className="items-center justify-between" space="lg">
                                <Heading variant="lg">{title}</Heading>
                                {onClose && (
                                    <Tooltip label="Close">
                                        <ModalCloseButton testID="btn-close-modal">
                                            <CloseIcon color="blueGray.400" />
                                        </ModalCloseButton>
                                    </Tooltip>
                                )}
                            </HStack>
                            {subTitle && (
                                <>
                                    {typeof subTitle === "string" ||
                                    isReactElement(subTitle, Fragment) ? (
                                        <Text isParagraph variant="sm">
                                            {subTitle}
                                        </Text>
                                    ) : (
                                        subTitle
                                    )}
                                </>
                            )}
                        </VStack>
                    </ModalHeaderUI>
                )}
                {children && (
                    <ModalBodyUI hasFooter={!!footer} testID="modal-body">
                        {children}
                    </ModalBodyUI>
                )}

                {footer ? (
                    <ModalFooterUI hasChildren={!!children}>{footer}</ModalFooterUI>
                ) : (
                    (typeof onSubmit === "function" || typeof onReset === "function") && (
                        <ModalFooterUI className="justify-between" hasChildren={!!children}>
                            {onReset && (
                                <Button action="negative" variant="ghost" onPress={onReset}>
                                    <ButtonText>{btnTextReset}</ButtonText>
                                </Button>
                            )}
                            <HStack className="ml-auto" space="sm">
                                {onClose && (
                                    <Button
                                        action="secondary"
                                        variant="outline"
                                        onPress={handleClose}
                                    >
                                        <ButtonText>{btnTextCancel}</ButtonText>
                                    </Button>
                                )}
                                {onSubmit && (
                                    <Button
                                        aria-label={btnTextOk}
                                        isDisabled={isPrimaryButtonDisabled}
                                        testID="btn-submit"
                                        onPress={onSubmit}
                                    >
                                        {isLoading && <ButtonSpinner />}
                                        <ButtonText>{btnTextOk}</ButtonText>
                                    </Button>
                                )}
                            </HStack>
                        </ModalFooterUI>
                    )
                )}
            </ModalContent>
        </ModalUI>
    );
};

export default memo(Modal);
